/**
 * The parts every modal in the app shares: a way out.
 *
 * Trey, 20 Aug 2026: "there needs to be an x close button on the settings pop
 * up and all other pop ups as well. Should also just be able to click out of
 * the boxes too."
 *
 * Three ways out of every dialogue, which is the convention everywhere else on
 * the web and therefore the one an advisor already has in their fingers:
 *
 *   1. the × in the corner
 *   2. a click on the dim area outside the panel
 *   3. Escape
 *
 * In `ui/` rather than in a feature because Settings, Trip Settings and the
 * swatch palette all need it and a feature may not import another
 * (ARCHITECTURE.md §3).
 *
 * ── The click-outside trap ───────────────────────────────────────────────
 *
 * A `click` handler on the overlay fires for clicks INSIDE the panel too,
 * because the event bubbles up through it. Closing on that would make every
 * button in the dialogue also close the dialogue. `overlayCloser` compares
 * `e.target` with `e.currentTarget` and acts only when the click landed on the
 * overlay itself.
 *
 * That still is not quite enough. Press the mouse down on a text field, drag
 * to select, release outside the panel, and the browser reports a click whose
 * TARGET is the overlay — so selecting text would throw the form away. So the
 * press has to have started on the overlay as well.
 *
 * ── Why the original has none of this ────────────────────────────────────
 *
 * Legacy's settings modal has an explicit note that its overlay does NOT close
 * on a click (app.html:4445), and no × either: the only way out is Cancel. The
 * reasoning was presumably a mis-click discarding a form — which the
 * mousedown rule above answers, and which is in any case not a reason to
 * remove the two exits everybody expects.
 */

import { h } from '../lib/dom.js'

/**
 * The × in the corner.
 *
 * A real `<button>` with a label, not a `×` glyph in a div: the character
 * alone is announced as "times" or "multiplication sign" by a screen reader,
 * and a div is not focusable or reachable by keyboard at all.
 *
 * @param {() => void} onClose
 * @param {{label?: string}} [options]
 * @returns {HTMLElement}
 */
export function modalClose(onClose, options = {}) {
  return h(
    'button',
    {
      type: 'button',
      class: 'modal-close',
      'data-modal-close': '',
      'aria-label': options.label || 'Close',
      title: options.label || 'Close',
      onclick: () => onClose(),
    },
    h('span', { 'aria-hidden': 'true' }, '×')
  )
}

/**
 * A handler pair for the overlay, so a click on the dim area closes and a
 * click inside the panel does not.
 *
 * Returns BOTH events because the mousedown is half the rule: a drag that
 * starts inside the panel and ends outside it is text selection, not a
 * dismissal.
 *
 * @param {() => void} onClose
 * @returns {{onmousedown: (e: any) => void, onclick: (e: any) => void}}
 */
export function overlayCloser(onClose) {
  let startedOutside = false

  return {
    onmousedown(e) {
      startedOutside = e.target === e.currentTarget
    },
    onclick(e) {
      if (e.target !== e.currentTarget) return
      if (!startedOutside) return
      startedOutside = false
      onClose()
    },
  }
}

/**
 * Close on Escape, for as long as the dialogue is open.
 *
 * Bound on the document rather than the panel, because the key has to work
 * before anything inside has been focused. `capture` so a control that
 * swallows Escape — a `<select>` with its list open, a native date picker —
 * does not take the app's only keyboard exit with it.
 *
 * @param {() => void} onClose
 * @param {Document} [doc]
 * @returns {() => void} stop listening
 */
export function closeOnEscape(onClose, doc = globalThis.document) {
  if (!doc || typeof doc.addEventListener !== 'function') return () => {}
  const onKey = (/** @type {any} */ e) => {
    if (e.key !== 'Escape' && e.key !== 'Esc') return
    e.stopPropagation()
    onClose()
  }
  doc.addEventListener('keydown', onKey, true)
  return () => doc.removeEventListener('keydown', onKey, true)
}

/**
 * Stop the page behind a dialogue from scrolling, until it closes.
 *
 * The fourth copy of this is what put it here. `clients-modal.js:144`,
 * `leads-modal.js:109` and `account-modal.js:88` each hold their own, all
 * identical; the Financial Dashboard would have been the fourth. Those three
 * are not converted in the same change that adds this — a working modal is
 * not worth the risk of a mechanical edit — but they should be, and this is
 * the thing to convert them to.
 *
 * The previous value is captured and restored rather than being set back to
 * `'auto'`. For this app it is `''`, and `'auto'` is a value the page has
 * never had: writing it leaves an inline style behind that overrides the
 * stylesheet for the rest of the session.
 *
 * @param {Document} [doc]
 * @returns {() => void} let it scroll again
 */
export function lockScroll(doc = globalThis.document) {
  const body = doc && doc.body
  if (!body || !body.style) return () => {}
  const previous = body.style.overflow
  body.style.overflow = 'hidden'
  let released = false
  return () => {
    /* Idempotent: a teardown that runs twice must not restore `'hidden'`
       captured by its own first run. */
    if (released) return
    released = true
    body.style.overflow = previous
  }
}
